// Sofort-Speichern einzelner Felder des Schadens (Spec §1): Kontakt, Risiko,
// Vertrag (und ab Task 9 die Abschluss-Spalten) gehen nicht über den
// Meldungs-Autosave, sondern sofort als Teil-PUT. Die PUTs laufen der Reihe
// nach — ein zweites `senden` wartet auf das erste —, damit der Server die
// Änderungen in der Reihenfolge der Eingabe sieht.
//
// Antworten für eine Id, die nicht mehr angezeigt wird, ändern weder den
// Zustand noch die Daten der Seite. Ein gescheiterter Patch bleibt offen und
// wird mit `wiederholen` erneut geschickt; spätere Patches mit denselben
// Feldern ersetzen ihn.
import * as React from "react";
import type { updateDamage } from "../api";
import type { DamagePatch, MappeDamage } from "../types";
import type { AutosaveZustand } from "./useMeldungAutosave";

const IDLE: AutosaveZustand = { status: "idle", fehler: null };

/** Felder eines Patches ohne die, die `ohne` setzt. */
function ohneFelder(patch: DamagePatch, ohne: DamagePatch): DamagePatch {
  const rest: Record<string, unknown> = { ...patch };
  for (const k of Object.keys(ohne)) delete rest[k];
  return rest as DamagePatch;
}

export function useSofortSpeichern(opts: {
  /** `null` vor der Anlage — dann wird nichts gespeichert. */
  damageId: number | null;
  /** = `updateDamage`. */
  speichern: typeof updateDamage;
  /** Bekommt den gespeicherten Schaden des Servers (nur für die aktuelle Id). */
  onGespeichert?: (damage: MappeDamage) => void;
}): {
  zustand: AutosaveZustand;
  /** Speichert den Patch nach allen vorher gesendeten. Lehnt nie ab; ein Fehler steht in `zustand`. */
  senden: (patch: DamagePatch) => Promise<void>;
  /** Schickt die gescheiterten Felder erneut; ohne Fehler ein No-op. */
  wiederholen: () => void;
} {
  const { damageId } = opts;
  const [zustand, setZustand] = React.useState<AutosaveZustand>(IDLE);

  const speichernRef = React.useRef(opts.speichern);
  const onGespeichertRef = React.useRef(opts.onGespeichert);
  React.useEffect(() => {
    speichernRef.current = opts.speichern;
    onGespeichertRef.current = opts.onGespeichert;
  });

  // Die angezeigte Id, nach dem Commit nachgeführt: Eine Antwort, die nach
  // einem Wechsel ankommt, gilt damit als veraltet. `senden` selbst nimmt die
  // Id aus seinem Render (siehe unten), nicht aus diesem Ref.
  const idRef = React.useRef(damageId);

  const ketteRef = React.useRef<Promise<void>>(Promise.resolve());
  /** Gescheiterte, noch nicht gespeicherte Felder — je Id. */
  const offenRef = React.useRef<{ id: number; patch: DamagePatch } | null>(null);

  React.useLayoutEffect(() => {
    if (idRef.current === damageId) return;
    idRef.current = damageId;
    // Offene Felder der alten Id verfallen (sie gehörten zu einem anderen Schaden).
    offenRef.current = null;
  }, [damageId]);

  // Id-Wechsel: neuer Zustand.
  const [letzteId, setLetzteId] = React.useState(damageId);
  if (letzteId !== damageId) {
    setLetzteId(damageId);
    setZustand(IDLE);
  }

  // Je Id ein eigenes `senden`: Wer es aus einem Render hat, speichert für die
  // Id dieses Renders — auch wenn die Ref noch nicht nachgeführt ist.
  const senden = React.useCallback((patch: DamagePatch): Promise<void> => {
    const id = damageId;
    if (id === null) return Promise.resolve();
    const aktuell = () => idRef.current === id;
    const lauf = ketteRef.current.then(async () => {
      if (aktuell()) setZustand({ status: "laeuft", fehler: null });
      try {
        const damage = await speichernRef.current(id, patch);
        if (!aktuell()) return;
        const offen = offenRef.current;
        if (offen && offen.id === id) {
          const rest = ohneFelder(offen.patch, patch);
          offenRef.current = Object.keys(rest).length > 0 ? { id, patch: rest } : null;
        }
        onGespeichertRef.current?.(damage);
        setZustand(
          offenRef.current
            ? { status: "fehler", fehler: "Nicht alle Änderungen gespeichert" }
            : { status: "ok", fehler: null },
        );
      } catch (e: unknown) {
        if (!aktuell()) return;
        const offen = offenRef.current;
        offenRef.current = { id, patch: { ...(offen && offen.id === id ? offen.patch : {}), ...patch } };
        setZustand({ status: "fehler", fehler: e instanceof Error ? e.message : String(e) });
      }
    });
    ketteRef.current = lauf;
    return lauf;
  }, [damageId]);

  const wiederholen = React.useCallback(() => {
    const offen = offenRef.current;
    if (!offen || offen.id !== damageId) return;
    void senden(offen.patch);
  }, [senden, damageId]);

  return { zustand, senden, wiederholen };
}
